import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logoImg from "@/assets/logo.png";

const automotiveServices = [
  { name: "Window Tint", href: "/automotive/tint" },
  { name: "PPF & Color PPF", href: "/automotive/ppf" },
  { name: "Custom Wraps", href: "/automotive/wraps" },
  { name: "Ceramic Coating", href: "/automotive/ceramic" },
  { name: "Detailing", href: "/automotive/detailing" },
];
const commercialServices = [
  { name: "Tint & Privacy Film", href: "/commercial/tint" },
  { name: "Signs & Decals", href: "/commercial/signs" },
  { name: "Decorative Film", href: "/residential/decorative" },
  { name: "Vehicle Wraps", href: "/automotive/wraps#commercial-fleet-wraps" },
];
const residentialServices = [
  { name: "Tint & Privacy Film", href: "/residential/tint" },
  { name: "Countertop & Appliance PF", href: "/residential/countertop" },
  { name: "Decorative Film", href: "/residential/decorative" },
];
const marineServices = [
  { name: "PPF & Ceramic", href: "/marine/ppf" },
  { name: "Detailing", href: "/automotive/detailing" },
];

const mobileMenuSections = [
  { label: "About", items: [{ name: "About Us", href: "/about" }, { name: "All Services", href: "/services" }, { name: "Blog", href: "/blog" }] },
  { label: "Automotive", items: automotiveServices },
  { label: "Commercial", items: commercialServices },
  { label: "Residential", items: residentialServices },
  { label: "Marine & Aviation", items: marineServices },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const location = useLocation();
  const isActive = (path: string) => location.pathname.startsWith(path);

  const toggleMobileSection = (label: string) => {
    setMobileExpanded(prev => (prev === label ? null : label));
  };

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileExpanded(null);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/5 backdrop-blur-[2px] border-b border-white/5">
      <div className="container flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center" onClick={closeMobile}>
          <img alt="Hyper Haus" className="h-10 md:h-12 w-auto" width={85} height={48} src={logoImg} />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          {/* About Dropdown */}
          <div className="relative" onMouseEnter={() => setActiveDropdown("about")} onMouseLeave={() => setActiveDropdown(null)}>
            <button className={`nav-link text-sm uppercase tracking-wider flex items-center gap-1 ${isActive("/about") || isActive("/blog") ? "text-foreground" : ""}`}>
              About <ChevronDown className="w-3 h-3" />
            </button>
            <AnimatePresence>
              {activeDropdown === "about" && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="absolute top-full left-0 pt-2">
                  <div className="bg-card border border-border rounded-lg p-2 min-w-[200px]">
                    <Link to="/about" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors">About Us</Link>
                    <Link to="/services" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors">All Services</Link>
                    <Link to="/blog" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors">Blog</Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Automotive Dropdown */}
          <div className="relative" onMouseEnter={() => setActiveDropdown("automotive")} onMouseLeave={() => setActiveDropdown(null)}>
            <button className={`nav-link text-sm uppercase tracking-wider flex items-center gap-1 ${isActive("/automotive") ? "text-foreground" : ""}`}>
              Automotive <ChevronDown className="w-3 h-3" />
            </button>
            <AnimatePresence>
              {activeDropdown === "automotive" && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="absolute top-full left-0 pt-2">
                  <div className="bg-card border border-border rounded-lg p-2 min-w-[200px]">
                    {automotiveServices.map(service => (
                      <Link key={service.href} to={service.href} className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors">
                        {service.name}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Commercial Dropdown */}
          <div className="relative" onMouseEnter={() => setActiveDropdown("commercial")} onMouseLeave={() => setActiveDropdown(null)}>
            <button className="nav-link text-sm uppercase tracking-wider flex items-center gap-1">
              Commercial <ChevronDown className="w-3 h-3" />
            </button>
            <AnimatePresence>
              {activeDropdown === "commercial" && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="absolute top-full left-0 pt-2">
                  <div className="bg-card border border-border rounded-lg p-2 min-w-[200px]">
                    {commercialServices.map(service => (
                      <Link key={service.href} to={service.href} className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors">
                        {service.name}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Residential Dropdown */}
          <div className="relative" onMouseEnter={() => setActiveDropdown("residential")} onMouseLeave={() => setActiveDropdown(null)}>
            <button className="nav-link text-sm uppercase tracking-wider flex items-center gap-1">
              Residential <ChevronDown className="w-3 h-3" />
            </button>
            <AnimatePresence>
              {activeDropdown === "residential" && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="absolute top-full left-0 pt-2">
                  <div className="bg-card border border-border rounded-lg p-2 min-w-[200px]">
                    {residentialServices.map(service => (
                      <Link key={service.href} to={service.href} className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors">
                        {service.name}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Marine Dropdown */}
          <div className="relative" onMouseEnter={() => setActiveDropdown("marine")} onMouseLeave={() => setActiveDropdown(null)}>
            <button className="nav-link text-sm uppercase tracking-wider flex items-center gap-1">
              Marine & Aviation <ChevronDown className="w-3 h-3" />
            </button>
            <AnimatePresence>
              {activeDropdown === "marine" && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="absolute top-full left-0 pt-2">
                  <div className="bg-card border border-border rounded-lg p-2 min-w-[200px]">
                    {marineServices.map(service => (
                      <Link key={service.href} to={service.href} className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors">
                        {service.name}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link to="/training" className="nav-link text-sm uppercase tracking-wider">
            Training
          </Link>
        </nav>

        {/* CTA & Mobile Toggle */}
        <div className="flex items-center gap-4">
          <a href="tel:3109469046" className="hidden md:flex items-center gap-2 text-primary hover:text-primary/80 transition-colors">
            <Phone className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary-foreground">(310) 946-9046</span>
          </a>
          <Link to="/contact" className="hidden sm:block btn-hero-primary text-xs py-3 px-6">
            Request Consultation
          </Link>
          <button className="lg:hidden p-2" onClick={() => { setMobileOpen(!mobileOpen); setMobileExpanded(null); }} aria-label="Toggle menu">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-background border-t border-border overflow-hidden"
          >
            <nav className="container py-4 max-h-[calc(100vh-5rem)] overflow-y-auto">
              {mobileMenuSections.map((section) => (
                <div key={section.label} className="border-b border-border/30">
                  <button
                    onClick={() => toggleMobileSection(section.label)}
                    className="flex items-center justify-between w-full py-4 text-foreground"
                  >
                    <span className="text-sm font-semibold uppercase tracking-wider">
                      {section.label}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${
                        mobileExpanded === section.label ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <AnimatePresence>
                    {mobileExpanded === section.label && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="pb-3 pl-4 space-y-1">
                          {section.items.map((item) => (
                            <Link
                              key={item.href}
                              to={item.href}
                              onClick={closeMobile}
                              className="block py-2.5 px-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}

              <Link to="/training" className="block py-4 text-sm font-semibold uppercase tracking-wider text-foreground border-b border-border/30" onClick={closeMobile}>
                Training
              </Link>

              <div className="pt-4 space-y-3">
                <Link to="/contact" className="block w-full btn-hero-primary text-xs py-3 px-6 text-center" onClick={closeMobile}>
                  Request Consultation
                </Link>
                <a href="tel:3109469046" className="flex items-center justify-center gap-2 py-3 text-primary">
                  <Phone className="w-4 h-4" />
                  <span className="text-sm font-medium">(310) 946-9046</span>
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;